import React from 'react';

import classNames from 'classnames';

/**
 * A compact two-plus-option segmented switch.
 *
 * Variants:
 *   - `default` — the app's existing `in-nav` pill pattern (blue active),
 *     content-width, matching Tabs and the rest of the UI.
 *   - `dark` — a white bordered track whose options grow to fill the container
 *     (equal-width, no horizontal scroll); the active option is solid charcoal,
 *     inactive options have no background. Matches the IB-380 "Viewing as" toggle.
 *   - `inset` — a recessed grey track sized to its content (so long labels such as
 *     "Department + Cost Centre" are never truncated); the active option is solid
 *     blue. Matches the IB-834 Actuals-vs-Budget configuration design.
 *   - `track` — one continuous hairline-bordered track with the options butted
 *     together and no padding around them; the active option is a solid blue
 *     block running to the track's edges. This is the PM redesign wireframe's
 *     `.seg`, used by the Project Plan and Milestone Roadmap controls, and its
 *     values are the wireframe's own rather than the design system's: a 13px
 *     label, 6px/14px padding, and an `#e3e6ea` border.
 *
 * @param {Array<{value: string, label: string}>} options
 * @param {string} value  - the currently selected option value
 * @param {Function} onChange - called with the newly selected value
 * @param {'default'|'dark'|'inset'|'track'} [variant]
 * @param {boolean} [disabled] - blocks selection for keyboard and pointer alike, and
 *   announces the state. Opacity plus `pointer-events-none` on a wrapper only stops
 *   the mouse: the buttons stay tabbable and read as enabled to a screen reader.
 */
const VARIANTS = {
    default: {
        container: 'in-nav horizontal !tw-w-auto !tw-p-1 !tw-shadow-none tw-border tw-border-grey',
        item: (active) => classNames('in-nav__item !tw-h-8', active ? 'active' : 'inactive'),
    },
    dark: {
        container: 'tw-flex tw-w-full tw-gap-1 tw-p-1 tw-bg-white tw-border tw-border-grey tw-rounded-lg',
        item: (active) =>
            classNames(
                'tw-flex-1 tw-min-w-0 tw-truncate tw-h-8 tw-rounded-md tw-text-xs tw-font-medium tw-transition-colors',
                active ? 'tw-bg-charcoal tw-text-white' : 'tw-text-charcoal-75 hover:tw-bg-charcoal-10',
            ),
    },
    inset: {
        container: 'tw-inline-flex tw-gap-0.5 tw-p-[3px] tw-bg-grey/50 tw-rounded-[10px]',
        item: (active) =>
            classNames(
                'tw-px-[15px] tw-py-2 tw-rounded-lg tw-text-[13px] tw-transition-colors',
                active ? 'tw-bg-blue tw-text-white tw-font-medium' : 'tw-text-charcoal-75 tw-font-normal hover:tw-text-charcoal',
            ),
    },
    track: {
        // `overflow-hidden` is what lets the active block sit flush inside the rounded
        // track instead of squaring off its corners.
        container: 'tw-inline-flex tw-border tw-border-[#e3e6ea] tw-rounded-lg tw-overflow-hidden',
        item: (active) =>
            classNames(
                // 17px leading, so 6px of padding either side lands the option on the wireframe's
                // 29px — a <button> does not inherit the normal line height its <span> gets.
                'tw-px-3.5 tw-py-1.5 tw-text-[13px] tw-leading-[17px] tw-transition-colors',
                active ? 'tw-bg-blue tw-text-white' : 'tw-bg-white tw-text-charcoal-50 hover:tw-bg-charcoal-10',
            ),
    },
};

export default function SegmentedControl({ options = [], value, onChange, className, variant = 'default', disabled = false }) {
    const styles = VARIANTS[variant] || VARIANTS.default;

    return (
        <div className={classNames(styles.container, className, { 'tw-opacity-50': disabled })} role="tablist">
            {options.map((option) => {
                const active = option.value === value;

                return (
                    <button
                        key={option.value}
                        type="button"
                        role="tab"
                        aria-selected={active}
                        disabled={disabled}
                        onClick={() => onChange(option.value)}
                        className={classNames(styles.item(active), { 'tw-cursor-not-allowed': disabled })}
                    >
                        {option.label}
                    </button>
                );
            })}
        </div>
    );
}
